// LAYOUT-SPEC §0 — Responsibilities (the Teams page's second view)
//
//   ONE QUESTION  In this contract period, who owes each deliverable, and who
//                 only sees it?
//   PRIMARY OBJECT  The team × deliverable matrix for one period, grouped by
//                 team (Archetype B with groups).
//   PRIMARY VERB    Open a deliverable (row click).
//   WORST-CASE VOLUME  ~40 teams × ~400 deliverables (OMEGA ~1,100 cells);
//                 groups start collapsed, each header carries its counts.
//
// Read-only, composed from the reads the team pages already make: ListDuties
// (rules, routings, watches; and every duty's assignments, one call),
// ListWorkstreams with detail (each team's SOWs, one call) and the period's
// dates (one ListOccurrences). No per-team or per-deliverable call.
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraBadge, SpecraEntityCell, SpecraRecordGroupHeader, SpecraSegmentedControl, SpecraSelect, SpecraText, SpecraTooltip } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { Grid3x3 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { EmptyState, ErrorState } from '../../_shared/components/PageStates';
import { OccurrenceWindow, useDutiesDetail, useOccurrences, useWorkstreamDetails } from '../hooks';
import { cmDateShort, todayISO } from '../lib/format';
import { buildResponsibilityMatrix, countKinds, MATRIX_KINDS, nextDate } from '../lib/responsibilityMatrix';
import { cadencePhrase, describeRule, isAsNeeded } from '../lib/rule';
import { useCmLookups } from '../lookups';
import { CmTable } from './common';
const KIND_LABEL = {
    owes: 'Owes',
    copy: 'Own copy',
    inherited: 'Inherited',
    sees: 'Sees',
    not_owed: 'Not owed'
};
const KIND_HELP = {
    owes: 'This team owes it and files it.',
    copy: 'The SOW sends it to the platform level; this team still owes its own dated copy.',
    inherited: 'An exercised optional task this team performs brings it in; no SOW rule of its own.',
    sees: 'The SOW routed it to another team. This team sees those dates read-only and files nothing.',
    not_owed: 'The SOW says this team does not owe it.'
};
const KIND_VARIANT = {
    owes: 'info',
    copy: 'violet',
    inherited: 'outline',
    sees: 'neutral',
    not_owed: 'neutral'
};
/** The cadence in a few words: "Monthly", "Once", "As needed". */ function cadenceWords(r) {
    if (!r) return 'No rule';
    if (r.kind === 'dated') return 'Once';
    if (r.kind === 'event') return isAsNeeded(r.eventKind) ? 'As needed' : 'On an event';
    const c = cadencePhrase(r);
    return c ? c[0].toUpperCase() + c.slice(1) : 'Recurring';
}
function kindCell(row) {
    const quote = row.notOwedReason ?? row.quote;
    return /*#__PURE__*/ _jsx(SpecraTooltip, {
        content: /*#__PURE__*/ _jsxs("div", {
            className: "max-w-xs space-y-1",
            children: [
                /*#__PURE__*/ _jsx("div", {
                    children: KIND_HELP[row.kind]
                }),
                row.fullRule ? /*#__PURE__*/ _jsx("div", {
                    children: describeRule(row.fullRule)
                }) : null,
                quote ? /*#__PURE__*/ _jsxs("div", {
                    className: "italic",
                    children: [
                        "“",
                        quote,
                        "”"
                    ]
                }) : null,
                row.locator ? /*#__PURE__*/ _jsx("div", {
                    className: "opacity-80",
                    children: row.locator
                }) : null
            ]
        }),
        children: /*#__PURE__*/ _jsx(SpecraBadge, {
            variant: KIND_VARIANT[row.kind],
            tabIndex: 0,
            "data-testid": Testids.cmTeams.matrixKind,
            "data-kind": row.kind,
            children: KIND_LABEL[row.kind]
        })
    });
}
export function ResponsibilityMatrix({ programId }) {
    const navigate = useNavigate();
    const lk = useCmLookups(programId);
    const today = todayISO();
    const dated = lk.periods.filter((p)=>p.start && p.end);
    const current = dated.find((p)=>p.start <= today && today <= p.end) ?? dated[dated.length - 1];
    const [picked, setPicked] = useState('');
    const period = dated.find((p)=>p.id === picked) ?? current;
    const [kind, setKind] = useState('all');
    const [open, setOpen] = useState([]);
    const details = useDutiesDetail(programId);
    const teams = useWorkstreamDetails(programId);
    const occs = useOccurrences(programId, {
        window: OccurrenceWindow.CONTRACT_PERIOD,
        periodOfPerformanceId: period?.id ?? ''
    }, {
        enabled: !!period
    });
    const cells = useMemo(()=>{
        if (!period || !details.data || !teams.data || !occs.data) return undefined;
        const assignments = [];
        for (const d of details.data.byId.values())assignments.push(...d.assignments);
        const sows = teams.data.flatMap((t)=>t.sows);
        return buildResponsibilityMatrix({
            dutyIds: lk.duties.map((d)=>d.id),
            rules: lk.rules,
            assignments,
            sows,
            watches: lk.watches,
            occurrences: occs.data.occurrences,
            window: {
                start: period.start,
                end: period.end
            }
        });
    }, [
        period,
        details.data,
        teams.data,
        occs.data,
        lk.duties,
        lk.rules,
        lk.watches
    ]);
    const groups = useMemo(()=>{
        if (!cells) return [];
        const ruleById = new Map(lk.rules.map((r)=>[
                r.id,
                r
            ]));
        const byTeam = new Map();
        for (const c of cells){
            const duty = lk.dutyById.get(c.dutyId);
            if (!duty) continue;
            const arr = byTeam.get(c.teamId) ?? [];
            arr.push({
                ...c,
                id: `${c.teamId}|${c.dutyId}`,
                title: duty.title,
                pwsRef: duty.pwsRef,
                fullRule: c.rule ? ruleById.get(c.rule.id) : undefined,
                next: nextDate(c, today)
            });
            byTeam.set(c.teamId, arr);
        }
        const out = [
            ...byTeam
        ].map(([teamId, rows])=>({
                teamId,
                name: lk.teamName(teamId),
                rows: rows.sort((a, b)=>MATRIX_KINDS.indexOf(a.kind) - MATRIX_KINDS.indexOf(b.kind) || (a.next || '9').localeCompare(b.next || '9') || a.title.localeCompare(b.title)),
                counts: countKinds(rows)
            }));
        return out.sort((a, b)=>!a.teamId !== !b.teamId ? a.teamId ? 1 : -1 : a.name.localeCompare(b.name));
    }, [
        cells,
        lk.rules,
        lk.dutyById,
        lk.teamName,
        today
    ]);
    const totals = useMemo(()=>cells ? countKinds(cells) : undefined, [
        cells
    ]);
    const columns = [
        {
            id: 'deliverable',
            header: 'Deliverable',
            cell: (r)=>/*#__PURE__*/ _jsx(SpecraEntityCell, {
                    title: r.title,
                    description: r.pwsRef ? `PWS ${r.pwsRef}` : undefined
                })
        },
        {
            id: 'kind',
            header: 'Responsibility',
            cell: kindCell
        },
        {
            id: 'owner',
            header: 'Owed by',
            cell: (r)=>r.kind === 'sees' ? lk.teamName(r.ownerTeamId ?? '') : r.kind === 'not_owed' ? /*#__PURE__*/ _jsx("span", {
                    className: "text-muted-foreground",
                    children: "No one here"
                }) : /*#__PURE__*/ _jsx("span", {
                    className: "text-muted-foreground",
                    children: "This team"
                })
        },
        {
            id: 'cadence',
            header: 'Cadence',
            cell: (r)=>r.kind === 'not_owed' ? '' : cadenceWords(r.fullRule)
        },
        {
            id: 'next',
            header: 'Next due',
            align: 'right',
            cell: (r)=>r.kind === 'not_owed' ? '' : r.next ? cmDateShort(r.next) : /*#__PURE__*/ _jsx("span", {
                    className: "text-muted-foreground",
                    children: "None this period"
                })
        },
        {
            id: 'dates',
            header: 'Dates',
            align: 'right',
            cell: (r)=>r.kind === 'not_owed' ? '' : r.dates.length
        }
    ];
    const error = details.error ?? teams.error ?? occs.error;
    const loading = lk.isLoading || !cells;
    const shown = groups.map((g)=>({
            ...g,
            rows: kind === 'all' ? g.rows : g.rows.filter((r)=>r.kind === kind)
        })).filter((g)=>g.rows.length > 0);
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-4",
        "data-testid": Testids.cmTeams.matrix,
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex flex-wrap items-center gap-3",
                children: [
                    /*#__PURE__*/ _jsx(SpecraSelect, {
                        size: "sm",
                        className: "w-auto",
                        "aria-label": "Contract period",
                        value: period?.id ?? '',
                        onChange: (e)=>setPicked(e.target.value),
                        "data-testid": Testids.cmTeams.matrixPeriod,
                        children: dated.map((p)=>/*#__PURE__*/ _jsx("option", {
                                value: p.id,
                                children: p.name
                            }, p.id))
                    }),
                    /*#__PURE__*/ _jsx(SpecraSegmentedControl, {
                        size: "sm",
                        label: "Responsibility",
                        value: kind,
                        onValueChange: setKind,
                        testId: Testids.cmTeams.matrixKinds,
                        options: [
                            {
                                value: 'all',
                                label: 'All',
                                count: cells?.length
                            },
                            ...MATRIX_KINDS.map((k)=>({
                                    value: k,
                                    label: KIND_LABEL[k],
                                    count: totals?.[k]
                                }))
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs(SpecraText, {
                as: "p",
                size: "caption",
                tone: "muted",
                children: [
                    "Who owes each deliverable in ",
                    period?.name ?? 'this period',
                    ", by team. Hover a responsibility to read the contract’s words; open a row to see its dates."
                ]
            }),
            error ? /*#__PURE__*/ _jsx(ErrorState, {
                error: error,
                title: "Could not load responsibilities",
                onRetry: ()=>{
                    void details.refetch();
                    void teams.refetch();
                    void occs.refetch();
                }
            }) : !period && !lk.isLoading ? /*#__PURE__*/ _jsx(EmptyState, {
                icon: Grid3x3,
                title: "No dated contract periods",
                description: "Responsibilities read one contract period at a time. Add the periods’ dates on Periods to see who owes what."
            }) : loading ? /*#__PURE__*/ _jsx(SpecraText, {
                as: "p",
                size: "caption",
                tone: "muted",
                children: "Loading responsibilities…"
            }) : shown.length === 0 ? /*#__PURE__*/ _jsx(EmptyState, {
                icon: Grid3x3,
                title: kind === 'all' ? 'Nothing owed this period' : `No “${KIND_LABEL[kind]}” cells`,
                description: kind === 'all' ? 'No team has a deliverable or a SOW handling in this period. Upload a SOW on a team to fill it.' : 'Pick “All” to see every responsibility.'
            }) : shown.map((g)=>{
                const isOpen = open.includes(g.teamId);
                const panel = `matrix-${g.teamId || 'contract-wide'}`;
                const summary = MATRIX_KINDS.filter((k)=>g.counts[k] > 0).map((k)=>`${g.counts[k]} ${KIND_LABEL[k].toLowerCase()}`).join(' · ');
                return /*#__PURE__*/ _jsxs("section", {
                    className: "space-y-2",
                    children: [
                        /*#__PURE__*/ _jsx(SpecraRecordGroupHeader, {
                            variant: "hairline",
                            title: g.name,
                            count: g.rows.length,
                            description: summary,
                            open: isOpen,
                            controls: panel,
                            "data-testid": Testids.cmTeams.matrixGroup,
                            "data-team-id": g.teamId,
                            onClick: ()=>setOpen((o)=>o.includes(g.teamId) ? o.filter((t)=>t !== g.teamId) : [
                                        ...o,
                                        g.teamId
                                    ])
                        }),
                        isOpen ? /*#__PURE__*/ _jsx("div", {
                            id: panel,
                            children: /*#__PURE__*/ _jsx(CmTable, {
                                caption: `${g.name} responsibilities`,
                                rows: g.rows,
                                columns: columns,
                                rowKey: (r)=>r.id,
                                rowTestId: Testids.cmTeams.matrixRow,
                                getRowLabel: (r)=>r.title,
                                onRowClick: (r)=>navigate(`/delivery/programs/${programId}/deliverables/${r.dutyId}`)
                            })
                        }) : null
                    ]
                }, g.teamId || 'contract-wide');
            })
        ]
    });
}
